/**
 * BIRHAUS v3.0 Shadow System
 * Subtle, elegant depth with color hints and soft elevation
 */

export const shadowsV3 = {
  none: 'none',
  
  // Colored glows for interactive states
  'glow-primary': '0 0 40px -10px rgba(31, 111, 235, 0.25)',
  'glow-success': '0 0 40px -10px rgba(16, 185, 129, 0.25)',
  'glow-warning': '0 0 40px -10px rgba(245, 158, 11, 0.25)',
  'glow-error': '0 0 40px -10px rgba(239, 68, 68, 0.25)',
  'glow-info': '0 0 40px -10px rgba(59, 130, 246, 0.25)',
  'glow-purple': '0 0 40px -10px rgba(139, 92, 246, 0.25)',
  'glow-pink': '0 0 40px -10px rgba(236, 72, 153, 0.25)',
  
  // Subtle colored glows for hover states
  'glow-primary-subtle': '0 0 24px -8px rgba(31, 111, 235, 0.15)',
  'glow-success-subtle': '0 0 24px -8px rgba(16, 185, 129, 0.15)',
  'glow-warning-subtle': '0 0 24px -8px rgba(245, 158, 11, 0.15)',
  'glow-error-subtle': '0 0 24px -8px rgba(239, 68, 68, 0.15)',
  
  // Elevation shadows (softer, larger than traditional)
  'elevation-1': '0 2px 8px -2px rgba(0, 0, 0, 0.04), 0 1px 4px -1px rgba(0, 0, 0, 0.02)',
  'elevation-2': '0 8px 24px -8px rgba(0, 0, 0, 0.06), 0 4px 12px -4px rgba(0, 0, 0, 0.03)',
  'elevation-3': '0 16px 48px -12px rgba(0, 0, 0, 0.08), 0 8px 24px -8px rgba(0, 0, 0, 0.04)',
  'elevation-4': '0 24px 64px -16px rgba(0, 0, 0, 0.10), 0 12px 32px -12px rgba(0, 0, 0, 0.05)',
  'elevation-5': '0 32px 80px -20px rgba(0, 0, 0, 0.12), 0 16px 40px -16px rgba(0, 0, 0, 0.06)',
  'elevation-6': '0 40px 96px -24px rgba(0, 0, 0, 0.14), 0 20px 48px -20px rgba(0, 0, 0, 0.07)',
  
  // Inner shadows for pressed/inset states
  'inner-sm': 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  'inner-md': 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  'inner-lg': 'inset 0 4px 8px 0 rgba(0, 0, 0, 0.08)',
  'inner-xl': 'inset 0 6px 12px 0 rgba(0, 0, 0, 0.10)',
  
  // Focus rings with generous size
  'ring-primary': '0 0 0 3px rgba(31, 111, 235, 0.1)',
  'ring-success': '0 0 0 3px rgba(16, 185, 129, 0.1)',
  'ring-warning': '0 0 0 3px rgba(245, 158, 11, 0.1)',
  'ring-error': '0 0 0 3px rgba(239, 68, 68, 0.1)',
  'ring-info': '0 0 0 3px rgba(59, 130, 246, 0.1)',
  
  // Larger focus rings for accessibility
  'ring-primary-lg': '0 0 0 4px rgba(31, 111, 235, 0.15)',
  'ring-success-lg': '0 0 0 4px rgba(16, 185, 129, 0.15)',
  'ring-warning-lg': '0 0 0 4px rgba(245, 158, 11, 0.15)',
  'ring-error-lg': '0 0 0 4px rgba(239, 68, 68, 0.15)',
  
  // Simple glow reference (alias)
  glow: '0 0 40px -10px rgba(31, 111, 235, 0.25)',
} as const

// Component-specific shadow mappings
export const componentShadows = {
  button: {
    default: shadowsV3['elevation-1'],
    hover: shadowsV3['elevation-2'],
    active: shadowsV3['inner-sm'],
    focus: shadowsV3['ring-primary'],
    glow: shadowsV3['glow-primary-subtle'],
  },
  
  card: {
    default: shadowsV3['elevation-2'],
    hover: shadowsV3['elevation-3'],
    elevated: shadowsV3['elevation-4'],
    floating: shadowsV3['elevation-5'],
  },
  
  input: {
    default: shadowsV3['elevation-1'],
    focus: shadowsV3['ring-primary'],
    error: shadowsV3['ring-error'],
    success: shadowsV3['ring-success'],
  },
  
  modal: {
    backdrop: shadowsV3['elevation-6'],
    content: shadowsV3['elevation-5'],
  },
  
  dropdown: {
    default: shadowsV3['elevation-3'],
    floating: shadowsV3['elevation-4'],
  },
  
  tooltip: {
    default: shadowsV3['elevation-2'],
  },
  
  navigation: {
    default: shadowsV3['elevation-2'],
    glass: shadowsV3['elevation-1'],
  },
  
  list: {
    default: shadowsV3['elevation-1'],
    hover: shadowsV3['elevation-2'],
  },
  
  table: {
    default: shadowsV3['elevation-1'],
    header: shadowsV3['elevation-2'],
  },
} as const

// Shadow utilities for different states
export const shadowStates = {
  resting: shadowsV3['elevation-1'],
  hovered: shadowsV3['elevation-2'],
  focused: shadowsV3['elevation-3'],
  pressed: shadowsV3['inner-sm'],
  disabled: shadowsV3.none,
  loading: shadowsV3['glow-primary-subtle'],
} as const